import React, {FC, memo, useEffect, useState} from "react";
import {Layout} from "antd";
import {Link} from "react-router-dom";
import {CloseOutlined, MenuOutlined} from "@ant-design/icons";
import {GetCompanyInfoQuery, useGetCompanyInfoQuery} from "../../generated/graphql";
import {URL_CHANGELOG} from "../../models/url";
import LogoTypographic from "../../assets/logo-typographic-white.svg";
import packageJson from "../../../../package.json";
import css from "./LayoutStandard.less";
import {LayoutStandardProps} from "./types";
import {subDomainShort} from "./utils/helpers";
import {useRoles} from "./hooks/useRoles";
import MainMenu from "./components/MainMenu";
import HelpMenu from "./components/HelpMenu";
import {menuWidth} from "../../styles/layout";
import Roles from "../../../shared/models/roles";
import {useThrottledFetchPolicy} from "../../hooks/useThrottledFetchPolicy";

const LayoutStandard: FC<LayoutStandardProps> = ({navigationIsOpen = true, secondLevelNavigation = null, main = null, pageTitle, secondLevelNavigationItems}) => {
    const [_navigationIsOpen, toggleNavigation] = useState(navigationIsOpen);
    const contentAreaClasses = [css.contentArea];
    const menuToggleClasses = [css.mainMenuToggle];
    const menuTextClasses = [css.mainMenuItemTextClosed];
    const isStage = subDomainShort() === "stage";
    // const isStage = true;

    if (_navigationIsOpen) {
        menuToggleClasses.push(css.mainMenuIsOpen);
        contentAreaClasses.push(css.withMenuOpen);
        menuTextClasses.push(css.mainMenuItemTextOpen);
    }

    const pollIntervallInMinutes = 5;
    const pollIntervallInMilliseconds = pollIntervallInMinutes * 60 * 1000;

    const [companyInfo, setCompanyInfo] = useState<GetCompanyInfoQuery["companyInfo"] | undefined>(undefined);
    // Hält Feature-Flags nach Abo-Änderungen frisch (früher nebenbei durch die TableToolBar)
    const {data: companyInfoData} = useGetCompanyInfoQuery({fetchPolicy: useThrottledFetchPolicy("GetCompanyInfo", 5 * 60 * 1000)});

    useEffect(() => {
        if (companyInfoData?.companyInfo) {
            setCompanyInfo(companyInfoData.companyInfo);
        }
    }, [companyInfoData]);

    const {highestRole, rolesOfCurrent, isInstructor} = useRoles(pollIntervallInMilliseconds);
    const helpLinksEnabled = companyInfo?.subscription?.featureHelpLinks === true;
    const currentUrl = window.location.pathname.replace("/69bca0e2-3aae-4ca5-9968-ea33f187938d", "");

    return (
        <>
            <Layout className={css.root}>
                <Layout.Header className={isStage ? css.stage : css.header}>
                    <button
                        type="button"
                        // type="text"
                        className={menuToggleClasses.join(" ")}
                        onClick={() => toggleNavigation(!_navigationIsOpen)}
                    >
                        {_navigationIsOpen ? <CloseOutlined style={{fontSize: "0.95em"}} /> : <MenuOutlined />}
                        {_navigationIsOpen ? "Menu" : ""}
                    </button>
                    {secondLevelNavigation !== null && (
                        <div className={css.secondLevelNavigation}>
                            {pageTitle !== undefined && <h2 className={css.pageTitle}>{pageTitle(_navigationIsOpen)}</h2>}
                            {secondLevelNavigation}
                        </div>
                    )}
                    <HelpMenu className={css.helpLink} showPageHelp={helpLinksEnabled} />
                    <div className={css.secondLevelNavigationLogo}>
                        <img src={LogoTypographic} alt="logo-typographic" />
                        <Link
                            to={{
                                pathname: URL_CHANGELOG.replace(":highestRole", highestRole),
                            }}
                        >
                            <div className={css.versionNumber}>{`V ${packageJson.version} ${subDomainShort()}`}</div>
                        </Link>
                    </div>
                </Layout.Header>
                <Layout className={css.contentAreaWrapper}>
                    <Layout.Sider
                        collapsed={!_navigationIsOpen}
                        collapsedWidth={38}
                        // collapsedWidth={138}
                        breakpoint="xxl"
                        onBreakpoint={(isClosed) => {
                            toggleNavigation(!isClosed);
                        }}
                        width={menuWidth}
                        trigger={null}
                    >
                        <MainMenu
                            className={isStage ? css.stage : undefined}
                            highestRole={highestRole}
                            rolesOfCurrent={rolesOfCurrent}
                            isInstructor={isInstructor}
                            menuTextClasses={menuTextClasses}
                            closed={!_navigationIsOpen}
                            companyInfo={companyInfo}
                            secondLevelNavigationItems={secondLevelNavigationItems}
                            navigationIsOpen={_navigationIsOpen}
                            isStage={isStage}
                        />
                    </Layout.Sider>
                    <Layout className={contentAreaClasses.join(" ")}>
                        <Layout id="modalmount">{main}</Layout>
                    </Layout>
                </Layout>
                {highestRole === Roles.Superuser && (
                    <div
                        style={{
                            // width: "100%",
                            display: "flex",
                            gap: 5,
                            position: "absolute",
                            bottom: 5,
                            right: 40,
                            padding: "2px 8px",
                            fontSize: "0.9em",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            color: "white",
                            borderRadius: "5px",
                        }}
                    >
                        <div>{`${currentUrl}`}</div>
                    </div>
                )}
            </Layout>
        </>
    );
};

export default memo(LayoutStandard);
